import './HvacResultSavingsExplanation.scss';
import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import clsx from 'clsx';
import { Decimal } from 'decimal.js-light';
import React, { PropsWithChildren, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { SavingsValue } from 'src/modules/common/components/SavingsValue';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';
import { CoinIcon } from 'src/modules/common/icons/CoinIcon';
import { ErrorIcon } from 'src/modules/common/icons/ErrorIcon';
import { WarningIcon } from 'src/modules/common/icons/WarningIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { BodySmall01 } from 'src/modules/common/typography/BodySmall01';
import { BodySmall02 } from 'src/modules/common/typography/BodySmall02';
import { Heading01 } from 'src/modules/common/typography/Heading01';
import { HvacNotification } from 'src/modules/hvac/types/HvacNotification';

type Props = {
  readonly results: Decimal | null;
  readonly insufficientInputs: ReadonlyArray<HvacNotification>;
  readonly incompleteResults: boolean;
};

export const HvacResultSavingsExplanation = ({
  results,
  insufficientInputs,
  incompleteResults,
  children,
}: PropsWithChildren<Props>): React.ReactElement => {
  const [isListOpened, setListOpened] = useState(false);

  const isError = insufficientInputs.length > 0;

  const isWarning = !isError && incompleteResults;

  const listPreviewCount = 3;

  const list = isListOpened ? insufficientInputs : insufficientInputs.slice(0, listPreviewCount);

  return (
    <div className="bp-hvac-result-savings-explanation">
      <div
        className={clsx({
          'bp-hvac-result-savings-explanation__tile': true,
          'bp-hvac-result-savings-explanation__tile--error': isError,
          'bp-hvac-result-savings-explanation__tile--warning': isWarning,
        })}
      >
        <div className="bp-hvac-result-savings-explanation__header">
          <CoinIcon/>

          <BodyMedium01>
            <FormattedMessage id="hvac/result/savings/potential/total"/>
          </BodyMedium01>
        </div>

        <div className="bp-hvac-result-savings-explanation__body">
          {(isError || isWarning) && (
            <div
              className="bp-hvac-result-savings-explanation__notifications"
              data-testid={`bp-hvac-result-savings-explanation-${isError ? 'error' : 'warning'}`}
            >
              <div className={clsx({
                'bp-hvac-result-savings-explanation__notifications-icon': true,
                'bp-hvac-result-savings-explanation__notifications-icon--error': isError,
                'bp-hvac-result-savings-explanation__notifications-icon--warning': isWarning,
              })}
              >
                {isError && (<ErrorIcon/>)}

                {isWarning && (<WarningIcon/>)}
              </div>

              <div className="bp-hvac-result-savings-explanation__notifications-contents">
                <BodySmall01>
                  {isError && (<FormattedMessage id="hvac/result/warning/missingInputs/header"/>)}

                  {isWarning && (<FormattedMessage id="hvac/result/warning/inaccurateResults/header"/>)}
                </BodySmall01>

                {isError && (
                  <ul className="bp-hvac-result-savings-explanation__notifications-list">
                    {list.map((notification) => (
                      <li
                        key={notification.path}
                        className="bp-hvac-result-savings-explanation__notifications-list-item"
                      >
                        <FormattedMessage
                          id="hvac/result/warning/missingInputs/description"
                          values={{
                            group: notification.group.title
                              ? notification.group.title
                              : <>{GROUP_NAMES[notification.group.group]}&nbsp;{notification.group.index + 1}</>,
                            area: AREA_NAMES[notification.area],
                            section: notification.section.number,
                            version: notification.version,
                          }}
                        />
                      </li>
                    ))}

                    {insufficientInputs.length > listPreviewCount && (
                      <button
                        type="button"
                        className="bp-hvac-result-savings-explanation__button"
                        onClick={() => setListOpened((prev) => !prev)}
                      >
                        <BodySmall01>
                          {isListOpened ? (
                            <FormattedMessage id="hvac/result/warning/missingInputs/showLess"/>
                          ) : (
                            <FormattedMessage id="hvac/result/warning/missingInputs/showMore"/>
                          )}
                        </BodySmall01>

                        <span className="bp-hvac-result-savings-explanation__button-icon">
                          {isListOpened ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                        </span>
                      </button>
                    )}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="bp-hvac-result-savings-explanation__savings">
            <Heading01>
              <SavingsValue value={results} precision={0}/> <FormattedMessage
                id="unit/kilowatt_hour"
                values={{ sub, sup }}
              />/
              <FormattedMessage id="unit/square_meter" values={{ sub, sup }}/>
            </Heading01>
          </div>
        </div>
      </div>

      {children}

      <div className="bp-hvac-result-savings-explanation__disclaimer">
        <BodySmall01>
          <FormattedMessage id="hvac/result/savings/potential/disclaimer/title"/>
        </BodySmall01>

        <BodySmall02 className="bp-hvac-result-savings-explanation__disclaimer-text">
          <FormattedHTMLMessage
            id="hvac/result/savings/potential/disclaimer"
          />
        </BodySmall02>
      </div>
    </div>
  );
};

const AREA_NAMES = {
  [HvacArea.HEATING]: <FormattedMessage id="hvac/tab/heating"/>,
  [HvacArea.COOLING]: <FormattedMessage id="hvac/tab/cooling"/>,
  [HvacArea.VENTILATION]: <FormattedMessage id="hvac/tab/ventilation"/>,
};
const GROUP_NAMES = {
  [HvacGroup.EMISSION]: <FormattedMessage id="hvac/form/groups/emission"/>,
  [HvacGroup.DISTRIBUTION]: <FormattedMessage id="hvac/form/groups/distribution"/>,
  [HvacGroup.GENERATION]: <FormattedMessage id="hvac/form/groups/generation"/>,
  [HvacGroup.AIR_HANDLER]: <FormattedMessage id="hvac/form/groups/air_handler"/>,
};
